import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { Search, Plus, SortAsc, Pencil } from "lucide-react";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Loading from "../../component/Loading/Loading";
import AlertMsg from '../../component/AlertMsg/AlertMsg';
import Pagination from "../../utils/Pagenation/Pagenation";
import useAxiosInstance from "../../services/useAxiosInstance";
import GlassmorphicModal from "../../component/ReusableComponents/GlassmorphicModal";
import { AiOutlineSortAscending } from "react-icons/ai";

const EmployeeList = () => {
  const axiosInstance = useAxiosInstance();
  const [employees, setEmployees] = useState([]);
  const managerIdFromState = useSelector((state) => state.auth.managerId);
  const designationId = 1;
  const roleId = useSelector((state) => state.auth.roleId);
  const communityId = useSelector((state) => state.auth.communityId);
  const userId = useSelector((state) => state.auth.userId);

  const [errors, setErrors] = useState({});
  const [designations, setDesignations] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  const employeeIdFromState = useSelector((state) => state.auth.employeeId) || 0;

  let managerId = managerIdFromState;
  let employeeId = employeeIdFromState;

  if (managerId === null) {
    managerId = employeeId;
    employeeId = 0;
  }

  const [managers, setManagers] = useState([]);
  const [salesManagerId, setSalesManagerId] = useState(managerId);
  const [loading, setLoading] = useState(false);

  const todayDate = new Date().toISOString();

  const [formData, setFormData] = useState({
    employeeDto: {
      activeStatus: true,
      dateOfJoining: "",
      designationId: 0,
      employeeId: 0,
      leavingDate: null,
      employeeNo: "",
      managerId: null,
      userId: 0,
    },
    userDto: {
      activeStatus: true,
      emailId: "",
      emailIdIsVerified: true,
      firstName: "",
      isClient: false,
      lastLogin: "",
      lastName: "",
      middleName: " ",
      mobileIsVerified: true,
      mobileNumber: "",
      otp: 0,
      password: " ",
      roleId: 0,
      createdDate: "",
      modifiedById: userId,
      modifiedDate: todayDate,
    },
  });

  const handleManagerChange = (salesManagerId) => {
    setSalesManagerId(salesManagerId);
    setCurrentPage(0);
  };

  const handlePhoneNumberChange = (value) => {
    const isValid = value && value.length >= 10;

    setFormData((prevFormData) => ({
      ...prevFormData,
      userDto: {
        ...prevFormData.userDto,
        mobileNumber: value || "",
      },
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      mobileNumber: isValid ? "" : "Phone number must be at least 10 digits",
    }));
  };

  useEffect(() => {
    axiosInstance
      .get(`/api/public/employee/getEmployeesByDesignation/${designationId}`)
      .then((response) => {
        setManagers(response.data);
      })
      .catch((error) => {
        console.error("There was an error fetching the sales managers!", error);
      });
  }, [designationId]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/api/public/employee/getEmployeeList`,
        {
          params: {
            managerId: salesManagerId || 0,
          },
        }
      );
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [salesManagerId]);

  useEffect(() => {
    axiosInstance
      .get(`/api/public/designation/getAll`)
      .then((response) => {
        setDesignations(response.data);
      })
      .catch((error) => {
        console.error("Error fetching designations:", error);
        setErrors("Failed to fetch designations");
      });
  }, []);

  const handleEdit = async (employeeId) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/public/employee/getOne/${employeeId}`);
      const data = response.data;

      const formDataUpdate = {
        employeeDto: {
          activeStatus: data.activeStatus.toString(),
          dateOfJoining: data.dateOfJoining,
          designationId: data.designation.designationid,
          employeeNo: data.employeeNo,
          managerId: data.managerId,
          leavingDate: data.leavingDate,
          employeeId: data.employeeId,
          userId: data.userId,
        },
        userDto: {
          activeStatus: data.user.activeStatus.toString(),
          emailId: data.user.emailId,
          emailIdIsVerified: data.user.emailIdIsVerified,
          firstName: data.user.firstName,
          isClient: data.user.isClient,
          lastLogin: data.user.lastLogin,
          lastName: data.user.lastName,
          middleName: data.user.middleName,
          mobileIsVerified: data.user.mobileIsVerified,
          mobileNumber: data.user.mobileNumber,
          otp: data.user.otp,
          password: data.user.password,
          roleId: data.user.roleId,
          createdDate: data.user.createdDate,
        },
      };
      setFormData(formDataUpdate);
      setModalOpen(true);
      setEditMode(true);
    } catch (error) {
      console.error("Error editing employee:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const updateNestedProperty = (formData, name, value) => {
      const dotIndex = name.indexOf(".");
      if (dotIndex !== -1) {
        const object = name.substring(0, dotIndex);
        const property = name.substring(dotIndex + 1);
        return {
          ...formData,
          [object]: {
            ...formData[object],
            [property]: value,
          },
        };
      } else {
        return {
          ...formData,
          [name]: value,
        };
      }
    };

    setFormData((prevFormData) => {
      let updatedFormData = updateNestedProperty(prevFormData, name, value);

      const currentDate = new Date().toISOString().split('T')[0];

      if (name === "userDto.activeStatus") {
        if (value === "true") {
          updatedFormData = updateNestedProperty(updatedFormData, "employeeDto.leavingDate", "");
          updatedFormData = updateNestedProperty(updatedFormData, "employeeDto.dateOfJoining", currentDate);
        } else {
          updatedFormData = updateNestedProperty(updatedFormData, "employeeDto.leavingDate", currentDate);
        }
      }

      return updatedFormData;
    });
  };

  const handleSubmit = async (e) => {
    setModalOpen(false);
    setLoading(true);
    e.preventDefault();
    const validationErrors = validateFormData(formData);
    if (Object.keys(validationErrors).length === 0) {
      try {
        const employeeIdsend = formData.employeeDto.employeeId;
        const userIdsend = formData.employeeDto.userId;

        const response = await axiosInstance.put(`/api/public/employee/update/${employeeIdsend}/${userIdsend}`, formData);

        if (response.status === 200) {
          fetchEmployees();
          setAlert({ visible: true, message: 'Employee updated successfully!', type: 'success' });
          console.log("Data submitted successfully:", response.data);
        } else {
          setAlert({ visible: true, message: 'Failed to update employee: Please try again', type: 'error' });
        }
      } catch (error) {
        console.error("Error submitting data:", error.message);
        setAlert({ visible: true, message: 'Error updating employee: Please try again', type: 'error' });
      } finally {
        setLoading(false);
      }
    } else {
      setErrors(validationErrors);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setErrors({});
    setModalOpen(false);
    setEditMode(false);
  };

  const validateFormData = (data) => {
    const errors = {};
    if (!data.userDto.firstName.trim()) {
      errors.firstName = "First name is required";
    }
    if (!data.userDto.emailId.trim()) {
      errors.emailId = "Email is required";
    } else if (!isValidEmail(data.userDto.emailId)) {
      errors.emailId = "Invalid email format";
    }
    if (!data.userDto.mobileNumber.trim()) {
      errors.mobileNumber = "Phone number is required";
    } else if (data.userDto.mobileNumber.length < 10) {
      errors.mobileNumber = "Phone number must be at least 10 digits";
    }
    return errors;
  };

  const isValidEmail = (emailId) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailId);
  };

  const handleSort = () => {
    const sortedRolePages = [...employees].sort((a, b) => {
      const pageNameA = a.firstName.toLowerCase();
      const pageNameB = b.firstName.toLowerCase();

      if (sortOrder === 'asc') {
        return pageNameA.localeCompare(pageNameB);
      } else {
        return pageNameB.localeCompare(pageNameA);
      }
    });

    setEmployees(sortedRolePages);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const filteredEmployees = employees.filter((employee) =>
    employee.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.employeeNo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pageCount = Math.ceil(filteredEmployees.length / itemsPerPage);
  const filteredData = filteredEmployees.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  // Modal footer with action buttons
  const editModalFooter = (
    <div className="flex justify-end space-x-2">
      <button
        type="button"
        onClick={handleCancel}
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
      >
        Save Changes
      </button>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen md:p-4">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          {alert.visible && (
            <AlertMsg
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert({ ...alert, visible: false })}
            />
          )}

          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Employee List</h1>
            <NavLink to="/add-employee" className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2">
              <Plus size={18} />
              <span className="flex">Add <span className="hidden md:flex"> &nbsp; Employee</span></span>
            </NavLink>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="p-4 flex flex-col md:flex-row items-start gap-2 md:gap-4">
              <div className="relative w-full md:w-80 ">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search Employee..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {roleId === 1000 && (
                <div className="w-full md:w-auto">
                  <select
                    id="salesManager"
                    value={salesManagerId}
                    onChange={(e) => handleManagerChange(Number(e.target.value))}
                    className="px-3 py-[11px] rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  >
                    <option value={0}>Select a manager</option>
                    {managers
                      .sort((a, b) => a.firstName.localeCompare(b.firstName))
                      .map((manager) => (
                        <option
                          key={manager.employeeId}
                          value={manager.employeeId}
                        >
                          {manager.firstName}
                        </option>
                      ))}
                  </select>
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-400">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider flex items-center">
                      Employee Name
                      <button className="inline-flex ml-1" onClick={handleSort} aria-label="Sort">
                        <AiOutlineSortAscending className="h-5 w-5" />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Employee Number</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Email Id</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Designation</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">No Data Found</td>
                    </tr>
                  ) : (
                    filteredData.map((employee, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employee.firstName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employee.employeeNo}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employee.emailId}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employee.designationName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${employee.activeStatus
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                            }`}>
                            {employee.activeStatus ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleEdit(employee.employeeId)}
                            className="text-gray-500 hover:text-gray-700"
                            title="Edit Employee"
                          >
                            <Pencil size={18} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Page {currentPage + 1} of {pageCount}
              </div>
              <div className="flex-1 flex justify-end">
                <Pagination
                  currentPage={currentPage}
                  pageCount={pageCount}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>
          </div>

          {/* Edit Modal using GlassmorphicModal */}
          <GlassmorphicModal
            isOpen={modalOpen}
            onClose={handleCancel}
            title="Edit Employee"
            size="lg"
            footer={editModalFooter}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  maxLength={100}
                  name="userDto.firstName"
                  value={formData.userDto.firstName}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.firstName ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Middle Name
                </label>
                <input
                  type="text"
                  maxLength={100}
                  name="userDto.middleName"
                  value={formData.userDto.middleName}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  maxLength={100}
                  name="userDto.lastName"
                  value={formData.userDto.lastName}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.lastName ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee No
                </label>
                <input
                  type="text"
                  maxLength={50}
                  name="employeeDto.employeeNo"
                  value={formData.employeeDto.employeeNo}
                  onChange={handleChange}
                  readOnly
                  className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Joining
                </label>
                <input
                  type="date"
                  name="employeeDto.dateOfJoining"
                  value={formData.employeeDto.dateOfJoining || ''}
                  onChange={handleChange}
                  readOnly
                  className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  maxLength={100}
                  name="userDto.emailId"
                  value={formData.userDto.emailId}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.emailId ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {errors.emailId && <p className="text-red-500 text-xs mt-1">{errors.emailId}</p>}
              </div>

              <div>
                <label className="w-full block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <PhoneInput
                  country={'us'}
                  maxLength={10}
                  value={formData.userDto.mobileNumber}
                  onChange={handlePhoneNumberChange}
                  inputProps={{
                    name: 'userDto.mobileNumber',
                    required: true,
                    autoFocus: true,
                    placeholder: 'Enter mobile number',
                    style: { padding: '20px 35px', width: '100%' },
                  }}
                  containerClass="phone-input-container"
                  inputClass={`w-full rounded-md border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.mobileNumber ? 'border-red-500' : 'border-gray-300'}`}
                  buttonStyle={{ border: "none", background: "transparent" }}
                />
                {errors.mobileNumber && <p className="text-red-500 text-xs mt-1">{errors.mobileNumber}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Designation <span className="text-red-500">*</span>
                </label>
                <select
                  name="employeeDto.designationId"
                  value={formData.employeeDto.designationId}
                  onChange={handleChange}
                  className={`w-full rounded-md border ${errors.designationId ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="">Select Designation</option>
                  {designations.map((designation) => (
                    <option
                      key={designation.designationId}
                      value={designation.designationid}
                    >
                      {designation.name}
                    </option>
                  ))}
                </select>
                {errors.designationId && <p className="text-red-500 text-xs mt-1">{errors.designationId}</p>}
              </div>

              {formData.employeeDto.designationId === 2 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reporting Manager <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="employeeDto.managerId"
                    value={formData.employeeDto.managerId}
                    onChange={handleChange}
                    className={`w-full rounded-md border ${errors.managerId ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="">Select Manager</option>
                    {managers.map((manager) => (
                      <option
                        key={manager.employeeId}
                        value={manager.employeeId}
                      >
                        {manager.firstName}
                      </option>
                    ))}
                  </select>
                  {errors.managerId && <p className="text-red-500 text-xs mt-1">{errors.managerId}</p>}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Active Status <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4 mt-2">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="userDto.activeStatus"
                      value="true"
                      checked={formData.userDto.activeStatus === 'true'}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="userDto.activeStatus"
                      value="false"
                      checked={formData.userDto.activeStatus === 'false'}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600"
                    />
                    <span className="ml-2 text-sm text-gray-700">Inactive</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leaving Date
                </label>
                <input
                  type="date"
                  name="employeeDto.leavingDate"
                  value={formData.employeeDto.leavingDate || ''}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mt-4 text-sm text-red-500">
              * Required fields
            </div>
          </GlassmorphicModal>
        </div>
      )}
    </div>
  );
};

export default EmployeeList;